import type { DocNode } from '@atlaskit/adf-schema';
import FabricAnalyticsListeners, {
  type AnalyticsWebClient,
} from '@atlaskit/analytics-listeners';
import { FlagsProvider } from '@atlaskit/flag';
import Image from '@atlaskit/image';
import { BlankObjectExperienceModal } from '@atlassian/conversation-assistant-blank-object-experience/blank-object-experience-modal';
import { useAnalytics } from '@databases/analytics';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import { bridgeEventEmitter, DatabaseStreamState } from '@databases/rovo-bridge';
import { container } from '@databases/service-container';
import React, { useCallback } from 'react';
import { IntlProvider } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags';
import {
  BoeSubmitPromptCommand,
  OpenRovoSidePanelCommand,
} from '@canvas/databases-bridge-api/commands';
import { CONFLUENCE_AFM_CLIENT_ID } from '@canvas/databases-bridge-api/types';
import { client } from '@canvas/rovo-content-bridge-api';

import databaseCwrModalDark from '../../../assets/database_cwr_modal_dark.svg';
import databaseCwrModalLight from '../../../assets/database_cwr_modal_light.svg';

import { useDatabaseSmartPrompts } from './useDatabaseSmartPrompts';

const messages = defineMessages({
  headerText: {
    id: 'database-blank-object-experience-modal.header-text',
    defaultMessage: 'Let\u2019s create a database',
    description: 'Heading text shown in the blank object experience modal for databases',
  },
});

interface DatabaseBlankObjectExperienceModalProps {
  onClose: () => void;
  /** Called when the user submits a prompt, before the modal is closed. */
  onSubmit?: () => void;
}

export const DatabaseBlankObjectExperienceModal = ({
  onClose,
  onSubmit,
}: DatabaseBlankObjectExperienceModalProps) => {
  const { formatMessage, locale } = useIntl();
  const siteId = getCloudId() ?? '';
  const { contentId } = useDatabaseContent();
  const panelManager = container.resolve('PanelManager');
  const { smartPrompts } = useDatabaseSmartPrompts();

  const analyticsClient = useAnalytics();
  const analyticsWebClient = analyticsClient?.client as AnalyticsWebClient | undefined;

  // The cohort exposure is already fired upstream when deciding whether to mount the
  // modal (see `DatabaseContentLoader`). Pass `fireExperimentExposure: false` here to
  // avoid double-firing.
  const cwrBoeVariant = getExperimentValue('cwrBlankObjectExperiment', 'cohort', {
    fireExperimentExposure: false,
  });

  const handleTemplateSelect = useCallback(() => {
    panelManager.showPanel('DatabaseSetupPanel', { initialTab: 'templates' });
    onClose();
  }, [panelManager, onClose]);

  const handleImport = useCallback(() => {
    panelManager.showPanel('DatabaseSetupPanel', { initialTab: 'import' });
    onClose();
  }, [panelManager, onClose]);

  const handleSubmit = useCallback(
    (prompt: DocNode) => {
      // Extract plain text from ADF DocNode by recursively walking the node tree
      let plainText = '';
      const walk = (node: any) => {
        if (node.type === 'text') {
          plainText += node.text ?? '';
        }
        if (node.content) {
          (node.content as any[]).forEach(walk);
        }
      };
      walk(prompt);

      onSubmit?.();
      onClose();

      // Subscribe once to stream completion and open the Rovo side panel when done.
      let hasSeenStreaming = false;
      const unsubscribe = DatabaseStreamState.getInstance().subscribe((state) => {
        if (state.isStreaming) {
          hasSeenStreaming = true;
        } else if (hasSeenStreaming) {
          unsubscribe();
          bridgeEventEmitter.sendEvent(OpenRovoSidePanelCommand, {
            senderId: client(`confluence-database-${contentId}`),
            receiverId: CONFLUENCE_AFM_CLIENT_ID,
          });
        }
      });

      bridgeEventEmitter.sendEvent(BoeSubmitPromptCommand, {
        senderId: client(`confluence-database-${contentId}`),
        receiverId: CONFLUENCE_AFM_CLIENT_ID,
        prompt: plainText.trim(),
        contentId,
      });
    },
    [onClose, onSubmit, contentId]
  );

  return (
    <IntlProvider locale={locale}>
      <FabricAnalyticsListeners client={analyticsWebClient}>
        <FlagsProvider>
          <BlankObjectExperienceModal
            headerText={formatMessage(messages.headerText)}
            illustration={
              <Image src={databaseCwrModalLight} srcDark={databaseCwrModalDark} alt="" />
            }
            onClose={onClose}
            onTemplateSelect={handleTemplateSelect}
            onImport={handleImport}
            contentType="database"
            variant={cwrBoeVariant}
            blankObjectExperienceProps={{
              siteId,
              smartPrompts,
              onSubmit: handleSubmit,
            }}
          />
        </FlagsProvider>
      </FabricAnalyticsListeners>
    </IntlProvider>
  );
};
